/**
 * Geometry for the corner badges an avatar carries — bot, online, verified,
 * private.
 *
 * Every number lives here because web and mobile drifted apart twice: mobile
 * had no size cap, so a large avatar grew a badge web would have held at 16px;
 * and web sized the online dot on its own smaller scale, so it came out a third
 * smaller than the bot badge sitting opposite it on the same avatar. Both
 * platforms now call getAvatarBadgeGeometry and there is nothing left to drift.
 */

/** Badge diameter as a fraction of the avatar. */
const BADGE_SCALE = 0.38;

/**
 * Badges stop growing here. Past roughly a 42px avatar a proportional badge
 * starts to dominate the image, so it holds at 16px and the avatar keeps
 * growing around it.
 */
const BADGE_MAX_PX = 16;

/** The glyph inside a badge, as a fraction of the badge. */
const ICON_SCALE = 0.8;

export const getAvatarBadgeSize = (size: number): number =>
  Math.min(size * BADGE_SCALE, BADGE_MAX_PX);

/**
 * Offset, in px, from the avatar's bounding-box edge to a corner badge whose
 * center lands exactly on the avatar circle's edge — half the badge over the
 * image, half outside it.
 *
 * The badge sits on a diagonal (bottom-right for online, bottom-left for bot,
 * top-right for verified/private), so its center has to be at 45° on the
 * circumference:
 *
 *   R  = size / 2                     avatar radius, center at (R, R)
 *   r  = badgeSize / 2                badge radius
 *   cx = R + R·cos45° = R + R/√2      badge center, and cy is the same
 *
 * The offset is what's left between that center (plus the badge's own radius)
 * and the box edge:
 *
 *   offset = size − cx − r = R(1 − 1/√2) − r
 *
 * Worked example, size 10 and badgeSize 3.8:
 *   R = 5, r = 1.9
 *   cx = 5 + 5/√2 = 8.54            → 5px from the center, i.e. on the edge
 *   offset = 5(1 − 0.7071) − 1.9 = −0.44
 * Negative is correct and expected: the badge's outer half hangs 0.44px past
 * the box, which is exactly the half that should not cover the image. The sign
 * flips at badgeSize = size(1 − 1/√2); above that threshold the badge is large
 * enough to reach past the corner, below it the badge fits inside the box with
 * its center still on the circle. Only the center distance is the invariant.
 *
 * Squaring the corner (`bottom: 0, right: 0`, as this used to be) puts the
 * badge center inside the circle instead of on it, so the badge eats more of
 * the image on one side than it shows on the other.
 *
 * Only valid for circular avatars. Every badge that uses it is user-only, and
 * user avatars are always circles — factiiis are 20%-rounded squares and carry
 * no badges.
 *
 * NOTE: the offset is measured against a box that is exactly `size` tall. If
 * the positioning ancestor is taller — an `inline-block` wrapper adds baseline
 * descender space below an `inline-block` child, for instance — the badge is
 * pushed off the edge by that difference. Anchor badges to an element with the
 * avatar's exact dimensions, or give the wrapper a flex formatting context.
 */
export const getAvatarBadgeOffset = (size: number, badgeSize: number): number =>
  (size / 2) * (1 - Math.SQRT1_2) - badgeSize / 2;

/**
 * Everything a caller needs to place a badge on a `size`-px avatar: the badge
 * diameter, the glyph inside it, and the edge offset. One call so a platform
 * can't take the size from here and compute the offset itself.
 */
export const getAvatarBadgeGeometry = (
  size: number
): { badgeSize: number; iconSize: number; offset: number } => {
  const badgeSize = getAvatarBadgeSize(size);
  return {
    badgeSize,
    iconSize: badgeSize * ICON_SCALE,
    offset: getAvatarBadgeOffset(size, badgeSize)
  };
};

/**
 * The four places something can sit on an avatar's rim.
 *
 * Badges already use all four — online bottom-right, bot bottom-left,
 * verified/founder/private top-right — and the profile editor's camera control
 * wants the same rim. Naming them keeps the caller from writing `top` and
 * `right` by hand and getting one of the signs wrong, which is how the editor
 * ended up with hand-tuned `translate-x-2 translate-y-6` numbers that only
 * looked right at one avatar size.
 */
export type AvatarCorner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

/**
 * The CSS offsets that put a round `elementSize`-px control on the rim of a
 * `size`-px avatar, centred on the circle at 45° — half over the image, half
 * outside it.
 *
 * The distance is the same for every corner because a circle is symmetric, so
 * this is {@link getAvatarBadgeOffset} plus the mapping from a corner to the
 * two CSS edges that carry it. Returned as absolute-position offsets, so the
 * caller writes `style={getAvatarCornerOffsets(...)}` and nothing else.
 *
 * `elementSize` is the control's own diameter, not a badge diameter: a camera
 * button is bigger than an online dot and still has to land its centre on the
 * same circle. Pass what you are actually drawing.
 *
 * The positioning ancestor must be exactly `size` px in both directions — see
 * the note on {@link getAvatarBadgeOffset}.
 */
export const getAvatarCornerOffsets = (
  size: number,
  elementSize: number,
  corner: AvatarCorner
): { top?: number; bottom?: number; left?: number; right?: number } => {
  const offset = getAvatarBadgeOffset(size, elementSize);
  const [vertical, horizontal] = corner.split('-') as [
    'top' | 'bottom',
    'left' | 'right'
  ];
  return { [vertical]: offset, [horizontal]: offset };
};
